/**
 * ComputeTab — Per-kind job stats table and daily completed/failed chart.
 */

import { useState, useEffect, useCallback } from 'react';
import { getAdminCompute, type ComputeStatsResult } from '../../api/client.js';
import { TrendChart, type MultiSeriesData } from './TrendChart.js';

// ============================================================================
// Helpers
// ============================================================================

function fmtDuration(s: number | null): string {
  if (s === null) return '—';
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

function fmtPct(v: number | null): string {
  if (v === null) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

// ============================================================================
// Spinner
// ============================================================================

function Spinner({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ============================================================================
// ComputeTab
// ============================================================================

const DAYS_OPTIONS = [7, 30, 90] as const;
type DaysOption = typeof DAYS_OPTIONS[number];

export function ComputeTab() {
  const [days, setDays] = useState<DaysOption>(30);
  const [data, setData] = useState<ComputeStatsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (d: DaysOption) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAdminCompute(d);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load compute stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(days);
  }, [days, fetchData]);

  // Build chart series from daily data
  const completedSeries = data
    ? data.daily.map((e) => ({ time: e.date, value: e.completed }))
    : [];
  const failedSeries = data
    ? data.daily.map((e) => ({ time: e.date, value: e.failed }))
    : [];

  const chartSeries: MultiSeriesData[] = [
    { label: 'Completed', color: '#22C55E', data: completedSeries },
    { label: 'Failed', color: '#EF4444', data: failedSeries },
  ];

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400 gap-3">
        <Spinner className="w-5 h-5" />
        <span>Loading compute stats...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <p className="text-red-400">{error}</p>
        <button
          onClick={() => { void fetchData(days); }}
          className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-200 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {DAYS_OPTIONS.map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                days === d
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
        <button
          onClick={() => { void fetchData(days); }}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg text-gray-200 transition-colors disabled:opacity-50"
        >
          {loading ? <Spinner /> : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
          Reload
        </button>
      </div>

      {data && (
        <>
          {/* Overall summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="text-xl font-bold text-white">{fmtPct(data.overall.successRate)}</div>
              <div className="text-sm text-gray-400 mt-1">Success Rate</div>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="text-xl font-bold text-white">{fmtDuration(data.overall.avgDurationS)}</div>
              <div className="text-sm text-gray-400 mt-1">Avg Duration</div>
            </div>
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="text-xl font-bold text-white">{fmtDuration(data.overall.p95DurationS)}</div>
              <div className="text-sm text-gray-400 mt-1">p95 Duration</div>
            </div>
          </div>

          {/* Per-kind table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-900 border-b border-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Kind</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Total</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Completed</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Failed</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Success%</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Avg</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">p50</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">p95</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Avg Queue Wait</th>
                </tr>
              </thead>
              <tbody>
                {data.byKind.map((row, i) => {
                  const successPct = row.total > 0
                    ? ((row.completed / row.total) * 100).toFixed(1)
                    : null;
                  return (
                    <tr key={row.kind} className={i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}>
                      <td className="px-3 py-2 text-gray-100 font-mono text-xs">{row.kind}</td>
                      <td className="px-3 py-2 text-gray-300 tabular-nums">{row.total.toLocaleString()}</td>
                      <td className="px-3 py-2 text-green-400 tabular-nums">{row.completed.toLocaleString()}</td>
                      <td className="px-3 py-2 tabular-nums">
                        <span className={row.failed > 0 ? 'text-red-400' : 'text-gray-400'}>
                          {row.failed.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-3 py-2 tabular-nums">
                        <span className={
                          successPct === null ? 'text-gray-500' :
                          Number(successPct) >= 95 ? 'text-green-400' :
                          Number(successPct) >= 80 ? 'text-yellow-400' : 'text-red-400'
                        }>
                          {successPct !== null ? `${successPct}%` : '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-400 tabular-nums">{fmtDuration(row.avgDurationS)}</td>
                      <td className="px-3 py-2 text-gray-400 tabular-nums">{fmtDuration(row.p50DurationS)}</td>
                      <td className="px-3 py-2 text-gray-400 tabular-nums">{fmtDuration(row.p95DurationS)}</td>
                      <td className="px-3 py-2 text-gray-400 tabular-nums">{fmtDuration(row.avgQueueWaitS)}</td>
                    </tr>
                  );
                })}
                {data.byKind.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-gray-500">No compute data</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Daily chart */}
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-4 text-xs">
              <span className="font-medium text-white">Daily Completed / Failed</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-green-400" />Completed</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-red-400" />Failed</span>
            </div>
            <TrendChart series={chartSeries} height={160} />
          </div>
        </>
      )}
    </div>
  );
}
